<template>
  <div class="content-main relative">
    <div class="content">
      <div class="header flex flex-col">
        <div>{{ $t("message.returns.title") }}</div>
        <div class="flex flex-row mt-4 gap-2 w-full overflow-x-auto h-12">
          <button
            v-for="(status, index) in availableStatus"
            :key="index"
            @click="selectStatus(status)"
            class="btn-clear-button whitespace-nowrap px-2 h-10"
            :class="{ selected: status == selectedStatus }"
          >
            {{ formatStatusLabel(status) }} ({{ returnsStatus[status] }})
          </button>
        </div>
        <div class="filters-row">
          <input
            v-model="filters.name"
            class="filter-input"
            placeholder="Name..."
          />
          <input
            v-model="filters.email"
            class="filter-input"
            placeholder="Email..."
          />
          <input
            v-model="filters.rma"
            class="filter-input"
            placeholder="RMA..."
          />
          <input
            v-model="filters.orderNumber"
            class="filter-input"
            placeholder="Order #..."
          />
        </div>
        <BaseMiniSpinner v-if="loading" class="mr-2" />
      </div>
      <div v-if="!loading && filteredReturns.length > 0">
        <table class="default-table text-center">
          <thead>
            <tr class="table100-head">
              <th class="sortable text-left" @click="changeSort('date')">
                {{ $t("message.returns.date") }}
                <span v-if="sort.col === 'date'">
                  <img
                    :src="require(`@/assets/icons/${arrowDirection}-arrow.svg`)"
                  />
                </span>
              </th>
              <th class="sortable" @click="changeSort('rma')">
                {{ $t("message.returns.rma") }}
                <span v-if="sort.col === 'rma'">
                  <img
                    :src="require(`@/assets/icons/${arrowDirection}-arrow.svg`)"
                  />
                </span>
              </th>
              <th class="sortable" @click="changeSort('orderNumber')">
                {{ $t("message.returns.order_number") }}
                <span v-if="sort.col === 'orderNumber'">
                  <img
                    :src="require(`@/assets/icons/${arrowDirection}-arrow.svg`)"
                  />
                </span>
              </th>
              <th
                class="sortable text-left"
                @click="changeSort('customerName')"
              >
                {{ $t("message.returns.customer") }}
                <span v-if="sort.col === 'customerName'">
                  <img
                    :src="require(`@/assets/icons/${arrowDirection}-arrow.svg`)"
                  />
                </span>
              </th>
              <th
                class="sortable text-left"
                @click="changeSort('customerEmail')"
              >
                {{ $t("message.returns.customer_email") }}
                <span v-if="sort.col === 'customerEmail'">
                  <img
                    :src="require(`@/assets/icons/${arrowDirection}-arrow.svg`)"
                  />
                </span>
              </th>
              <th class="sortable" @click="changeSort('status')">
                {{ $t("message.returns.status") }}
                <span v-if="sort.col === 'status'">
                  <img
                    :src="require(`@/assets/icons/${arrowDirection}-arrow.svg`)"
                  />
                </span>
              </th>
            </tr>
          </thead>
          <tbody>
            <tr
              v-for="item in paginatedReturns"
              :key="item.id"
              @click="selectReturn(item.id)"
              class="whitespace-nowrap border-separate"
            >
              <td class="whitespace-nowrap text-left">
                {{ formatDateWithHour(item.date) }}
              </td>
              <td>{{ item.rma }}</td>
              <td>{{ item.orderNumber }}</td>
              <td class="text-left">{{ item.customerName }}</td>
              <td class="text-left">{{ item.customerEmail }}</td>
              <td>{{ item.status }}</td>
            </tr>
          </tbody>
        </table>
      </div>
      <div v-else-if="!loading" class="flex justify-center py-10">
        {{ $t("message.returns.no_returns_found") }}
      </div>

      <div v-if="!loading && filteredReturns.length > 0" class="footer">
        <div class="totals">
          Showing {{ pageFrom }} to {{ pageTo }} of
          {{ filteredReturns.length }} results
        </div>
        <div class="pages">
          <select v-model="limitPerPage" @change="page = 1">
            <option :value="10">10</option>
            <option :value="20">20</option>
            <option :value="50">50</option>
            <option :value="100">100</option>
          </select>
          Per Page
        </div>
        <div class="pages">
          <a
            :class="{ disabled: page <= 1 }"
            class="page-link"
            @click="prevPage"
            >Previous</a
          >
          <div>Page {{ page }}</div>
          <a
            :class="{ disabled: pageTo >= filteredReturns.length }"
            class="page-link"
            @click="nextPage"
            >Next</a
          >
        </div>
      </div>

      <ReturnModal
        :returnId="selectedReturn"
        v-if="selectedReturn"
        @close="selectedReturn = null"
        @returnUpdated="onReturnUpdated"
      />
      <ReturnActionsModals
        :returnId="selectedReturnData && selectedReturnData.id"
        :returnData="selectedReturnData"
        :show-receive-modal.sync="showReceiveModal"
        :show-refund-modal.sync="showRefundModal"
        @returnUpdated="onReturnUpdated"
      />
    </div>
  </div>
</template>

<script>
import { formatDateWithHour } from "@/helpers/globalHelpers.js";
import { getReturns, getReturnsStatus } from "@/api/store/ReturnsService";
import ReturnModal from "@/components/order/ReturnModal.vue";
import ReturnActionsModals from "@/components/order/ReturnActionsModals.vue";

export default {
  components: { ReturnModal, ReturnActionsModals },
  data() {
    return {
      loading: true,
      returns: [],
      selectedStatus: "Requested",
      returnsStatus: {},
      selectedReturn: null,
      selectedReturnData: null,
      showReceiveModal: false,
      showRefundModal: false,
      filters: {
        name: "",
        email: "",
        rma: "",
        orderNumber: "",
        status: ""
      },
      sort: {
        col: "date",
        dir: "desc"
      },
      page: 1,
      limitPerPage: 20
    };
  },
  computed: {
    availableStatus() {
      if (!this.returnsStatus) return null;
      return Object.keys(this.returnsStatus);
    },
    arrowDirection() {
      return this.sort.dir === "desc" ? "down" : "up";
    },
    filteredReturns() {
      let result = this.returns;
      if (this.filters.name) {
        const v = this.filters.name.toLowerCase();
        result = result.filter(
          r => r.customerName && r.customerName.toLowerCase().includes(v)
        );
      }
      if (this.filters.email) {
        const v = this.filters.email.toLowerCase();
        result = result.filter(
          r => r.customerEmail && r.customerEmail.toLowerCase().includes(v)
        );
      }
      if (this.filters.rma) {
        const v = this.filters.rma.toLowerCase();
        result = result.filter(r => r.rma && r.rma.toLowerCase().includes(v));
      }
      if (this.filters.orderNumber) {
        const v = this.filters.orderNumber.toLowerCase();
        result = result.filter(
          r =>
            r.orderNumber &&
            String(r.orderNumber)
              .toLowerCase()
              .includes(v)
        );
      }
      if (this.filters.status) {
        const v = this.filters.status.toLowerCase();
        result = result.filter(
          r => r.status && r.status.toLowerCase().includes(v)
        );
      }
      const { col, dir } = this.sort;
      return [...result].sort((a, b) => {
        let aVal = a[col] != null ? a[col] : "";
        let bVal = b[col] != null ? b[col] : "";
        if (col === "date") {
          aVal = new Date(aVal).getTime() || 0;
          bVal = new Date(bVal).getTime() || 0;
        } else {
          aVal = String(aVal).toLowerCase();
          bVal = String(bVal).toLowerCase();
        }
        if (aVal < bVal) return dir === "asc" ? -1 : 1;
        if (aVal > bVal) return dir === "asc" ? 1 : -1;
        return 0;
      });
    },
    paginatedReturns() {
      const start = (this.page - 1) * this.limitPerPage;
      return this.filteredReturns.slice(start, start + this.limitPerPage);
    },
    pageFrom() {
      return this.filteredReturns.length === 0
        ? 0
        : (this.page - 1) * this.limitPerPage + 1;
    },
    pageTo() {
      return Math.min(
        this.page * this.limitPerPage,
        this.filteredReturns.length
      );
    }
  },
  watch: {
    selectedReturn(newVal) {
      const query = { ...this.$route.query };
      if (newVal) {
        query.returnOpened = newVal;
      } else {
        delete query.returnOpened;
      }
      this.$router.replace({ query }).catch(() => {});
    },
    filters: {
      deep: true,
      handler() {
        this.page = 1;
      }
    }
  },
  mounted() {
    const status = this.$route.query.status || "Requested";
    this.selectedStatus = status;
    if (status === "Completed") {
      this.sort.col = "date";
      this.sort.dir = "desc";
    }
    this.loadGlobalStatus();
    this.loadReturns();
    if (this.$route.query.returnOpened) {
      this.selectedReturn = this.$route.query.returnOpened;
    }
  },
  methods: {
    formatDateWithHour,
    formatStatusLabel(status) {
      const statusMap = {
        receivedInWarehouse: "Received in warehouse",
        Requested: "Requested",
        "In Transit": "In Transit",
        Completed: "Completed"
      };
      return statusMap[status] || status;
    },
    async loadGlobalStatus() {
      this.returnsStatus = await getReturnsStatus(this.selectedStatus);
    },
    async loadReturns() {
      this.loading = true;
      this.returns = await getReturns(this.selectedStatus).catch(() => []);
      this.loading = false;
    },
    selectStatus(status) {
      this.selectedStatus = status;
      this.page = 1;
      if (status === "Completed") {
        this.sort.col = "date";
        this.sort.dir = "desc";
      }
      const query = { ...this.$route.query, status };
      this.$router.replace({ query }).catch(() => {});
      this.loadReturns();
    },
    selectReturn(returnId) {
      const query = {
        ...this.$route.query,
        status: this.selectedStatus,
        returnOpened: returnId
      };
      this.$router.replace({ query }).catch(() => {});
      this.selectedReturn = returnId;
    },
    changeSort(col) {
      this.page = 1;
      if (col === this.sort.col) {
        this.sort.dir = this.sort.dir === "desc" ? "asc" : "desc";
      } else {
        this.sort.col = col;
        this.sort.dir = "desc";
      }
    },
    prevPage() {
      if (this.page > 1) this.page--;
    },
    nextPage() {
      if (this.pageTo < this.filteredReturns.length) this.page++;
    },
    openReceiveModal(returnId) {
      this.selectedReturnData = this.returns.find(r => r.id === returnId);
      this.showReceiveModal = true;
    },
    openRefundModal(returnId) {
      this.selectedReturnData = this.returns.find(r => r.id === returnId);
      this.showRefundModal = true;
    },
    onReturnUpdated() {
      this.loadReturns();
    }
  }
};
</script>

<style lang="scss" scoped>
@import "@/assets/style/css/generic-variables.scss";

.filters-row {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  padding: 8px 0 4px;

  .filter-input {
    border: 1px solid #d1d5db;
    border-radius: 6px;
    padding: 5px 10px;
    font-size: 13px;
    width: 150px;

    &:focus {
      outline: none;
      border-color: #93c5fd;
    }

    @media (max-width: 600px) {
      width: 100%;
    }
  }
}

.sortable {
  cursor: pointer;
  user-select: none;

  &:hover {
    background-color: rgba(0, 0, 0, 0.04);
  }

  img {
    display: inline;
    margin-left: 4px;
    vertical-align: middle;
    width: 12px;
    height: 12px;
  }
}

.footer {
  display: flex;
  flex-direction: row;
  width: 100%;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;
  gap: 6px;
  padding: 6px 4px;

  .totals {
    font-size: 13px;
    color: #6b7280;
  }

  .pages {
    display: flex;
    flex-direction: row;
    align-items: center;
    gap: 6px;

    select {
      padding: 2px 4px;
    }
  }

  .page-link {
    color: $link-color-1;
    cursor: pointer;

    &.disabled {
      opacity: 0.4;
      pointer-events: none;
    }
  }

  @media (max-width: 600px) {
    flex-direction: column;
    align-items: center;
    text-align: center;
  }
}
</style>
